"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEFAULT_CURRENCY = void 0;
exports.formatPrice = formatPrice;
exports.calculateSurfaceCoverage = calculateSurfaceCoverage;
exports.calculatePrice = calculatePrice;
exports.DEFAULT_CURRENCY = 'EGP';
const CURRENCY_CONFIG = {
    EGP: { locale: 'en-EG', symbol: 'EGP', position: 'suffix' },
    USD: { locale: 'en-US', symbol: '$', position: 'prefix' },
    EUR: { locale: 'de-DE', symbol: '€', position: 'suffix' },
    GBP: { locale: 'en-GB', symbol: '£', position: 'prefix' },
    SAR: { locale: 'ar-SA', symbol: 'SAR', position: 'suffix' },
    AED: { locale: 'ar-AE', symbol: 'AED', position: 'suffix' },
};
/**
 * Format a price amount with the given currency code.
 * Falls back to appending the currency code as a suffix.
 */
function formatPrice(amount, currency = exports.DEFAULT_CURRENCY) {
    const config = CURRENCY_CONFIG[currency];
    if (!config) {
        return `${amount.toLocaleString()} ${currency}`;
    }
    const formatted = amount.toLocaleString(config.locale);
    return config.position === 'prefix'
        ? `${config.symbol}${formatted}`
        : `${formatted} ${config.symbol}`;
}
/**
 * Calculate the coverage percentage of visible nodes on a surface.
 * Uses the unified bounding box of all visible nodes, clamped to surface bounds.
 */
function calculateSurfaceCoverage(nodes, surfaceDimensions) {
    const visibleNodes = nodes.filter((n) => n.visible);
    if (visibleNodes.length === 0)
        return 0;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const node of visibleNodes) {
        minX = Math.min(minX, node.position.x);
        minY = Math.min(minY, node.position.y);
        maxX = Math.max(maxX, node.position.x + node.size.width);
        maxY = Math.max(maxY, node.position.y + node.size.height);
    }
    // Clamp to surface bounds
    minX = Math.max(0, minX);
    minY = Math.max(0, minY);
    maxX = Math.min(surfaceDimensions.width, maxX);
    maxY = Math.min(surfaceDimensions.height, maxY);
    const boundingArea = Math.max(0, (maxX - minX) * (maxY - minY));
    const surfaceArea = surfaceDimensions.width * surfaceDimensions.height;
    if (surfaceArea === 0)
        return 0;
    return (boundingArea / surfaceArea) * 100;
}
/**
 * Calculate the full pricing for a design.
 */
function calculatePrice(pricingConfig, surfaces, dimensions, surfaceOptionSelections, basePrice, variantAdditionalPrice) {
    const hasPrint = Object.values(surfaces).some((nodes) => nodes.some((n) => n.visible));
    const surfaceDetails = Object.keys(surfaces).map((surface) => {
        const nodes = surfaces[surface];
        const visibleNodes = nodes.filter((n) => n.visible);
        const hasDesign = visibleNodes.length > 0;
        // Surface activation price
        const surfacePrice = hasDesign
            ? (pricingConfig.surfacePrices[surface] ?? 0)
            : 0;
        // Coverage bracket price
        let coveragePercent = 0;
        let coverageBracketLabel = null;
        let coveragePrice = 0;
        if (hasDesign && dimensions[surface]) {
            coveragePercent = calculateSurfaceCoverage(visibleNodes, dimensions[surface]);
            const brackets = pricingConfig.coverageBrackets[surface];
            if (brackets) {
                const bracket = brackets.find((b) => coveragePercent <= b.max);
                if (bracket) {
                    coverageBracketLabel = bracket.label;
                    coveragePrice = bracket.price;
                }
            }
        }
        // Surface options price
        let optionsPrice = 0;
        const optionDetails = [];
        const optionDefs = pricingConfig.surfaceOptions?.[surface] ?? [];
        for (const def of optionDefs) {
            const selectedValue = surfaceOptionSelections[surface]?.[def.key] ?? def.default;
            const choice = def.choices.find((c) => c.value === selectedValue);
            if (choice) {
                optionsPrice += choice.price;
                optionDetails.push({
                    key: def.key,
                    label: def.label,
                    value: choice.value,
                    valueLabel: choice.label,
                    price: choice.price,
                });
            }
        }
        return {
            surface,
            hasDesign,
            coveragePercent: Math.round(coveragePercent),
            coverageBracketLabel,
            surfacePrice,
            coveragePrice,
            optionsPrice,
            optionDetails,
            subtotal: surfacePrice + coveragePrice + optionsPrice,
        };
    });
    const printSubtotal = (hasPrint ? pricingConfig.basePrintPrice : 0) +
        surfaceDetails.reduce((sum, s) => sum + s.subtotal, 0);
    return {
        basePrice,
        basePrintPrice: hasPrint ? pricingConfig.basePrintPrice : 0,
        hasPrint,
        surfaces: surfaceDetails,
        printSubtotal,
        variantAdditionalPrice,
        total: basePrice + variantAdditionalPrice + printSubtotal,
    };
}
